import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icon/icon.component';
import { ToastService } from './toast.service';
import { Subscription } from 'rxjs';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule, IconComponent],
  template: `
    <div class="fixed top-4 right-4 z-50 space-y-2">
      <div
        *ngFor="let toast of toasts"
        [ngClass]="getToastClasses(toast.type)"
        class="flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg min-w-[320px] max-w-md animate-slide-in-right"
      >
        <app-icon
          [name]="getIcon(toast.type)"
          [className]="getIconClasses(toast.type)"
        ></app-icon>
        <p class="flex-1 text-sm font-medium">{{ toast.message }}</p>
        <button
          (click)="removeToast(toast.id)"
          class="text-current opacity-70 hover:opacity-100 transition-opacity"
        >
          <app-icon name="x" className="w-4 h-4"></app-icon>
        </button>
      </div>
    </div>
  `,
  styles: [`
    @keyframes slide-in-right {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    .animate-slide-in-right {
      animation: slide-in-right 0.3s ease-out;
    }
  `]
})
export class ToastComponent implements OnInit, OnDestroy {
  toasts: Toast[] = [];
  private subscription?: Subscription;

  constructor(private toastService: ToastService) {}

  ngOnInit(): void {
    this.subscription = this.toastService.toast$.subscribe(({ type, message, duration }) => {
      this.show(type, message, duration);
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  getToastClasses(type: ToastType): string {
    const baseClasses = 'border-l-4';
    const typeClasses = {
      success: 'bg-green-50 border-green-500 text-green-800',
      error: 'bg-red-50 border-red-500 text-red-800',
      warning: 'bg-yellow-50 border-yellow-500 text-yellow-800',
      info: 'bg-blue-50 border-blue-500 text-blue-800'
    };
    return `${baseClasses} ${typeClasses[type]}`;
  }

  getIcon(type: ToastType): string {
    const icons = {
      success: 'check-circle',
      error: 'alert-circle',
      warning: 'alert-triangle',
      info: 'info'
    };
    return icons[type];
  }

  getIconClasses(type: ToastType): string {
    const classes = {
      success: 'w-5 h-5 text-green-600',
      error: 'w-5 h-5 text-red-600',
      warning: 'w-5 h-5 text-yellow-600',
      info: 'w-5 h-5 text-blue-600'
    };
    return classes[type];
  }

  show(type: ToastType, message: string, duration: number = 5000): void {
    const id = Math.random().toString(36).substring(7);
    const toast: Toast = { id, type, message, duration };

    this.toasts.push(toast);

    if (duration > 0) {
      setTimeout(() => {
        this.removeToast(id);
      }, duration);
    }
  }

  removeToast(id: string): void {
    this.toasts = this.toasts.filter(t => t.id !== id);
  }

  success(message: string, duration?: number): void {
    this.show('success', message, duration);
  }

  error(message: string, duration?: number): void {
    this.show('error', message, duration);
  }

  warning(message: string, duration?: number): void {
    this.show('warning', message, duration);
  }

  info(message: string, duration?: number): void {
    this.show('info', message, duration);
  }
}
